import { ShortUrlTargetType } from '@today/types'
import { z } from 'zod'

// ─── Public-facing schema (generate input) ────────────────────────────────────

/**
 * An absolute `https` URL that is safe to 302-redirect a public visitor to.
 *
 * Two layers of open-redirect defence:
 *   1. `protocol: /^https$/` — only `https`; never `javascript:`/`data:`/`http:`.
 *   2. `.refine(no credentials)` — reject a `userinfo@host` authority such as
 *      `https://bank.com@evil.com`. Zod's URL parser accepts that (it is valid
 *      WHATWG syntax) but a browser navigates to the *host* (`evil.com`), so a
 *      first-party short link could point at an attacker's site while appearing
 *      to be the bank's domain. Rejecting any URL with a username/password
 *      closes that bypass. (`http:`, protocol-relative `//host`, and bare
 *      schemes are already rejected by layer 1.)
 */
const safeRedirectUrl = z.url({ protocol: /^https$/ }).refine((value) => {
	// Zod v4 runs refinements even when the base `z.url()` check already failed,
	// so `new URL(value)` can throw here on an input `z.url()` rejected (e.g. a
	// percent-encoded `@`). Treat an unparseable value as "not credential-free"
	// → the refine reports a validation error (422), never an unhandled 500.
	try {
		const parsed = new URL(value)
		return parsed.username === '' && parsed.password === ''
	} catch {
		return false
	}
}, 'URL must not contain credentials (userinfo)')

/**
 * Body for `POST /short-urls`. The tenant is taken from the authenticated
 * principal (never the body); the shortcode is generated server-side.
 *
 * Discriminated on `targetType`:
 *   - `offer` → `targetId` is a UUID (the offer).
 *   - `url`   → `targetUrl` is an absolute `https` URL with no credentials.
 */
export const shortUrlCreateSchema = z.discriminatedUnion('targetType', [
	z.object({
		targetType: z.literal(ShortUrlTargetType.OFFER),
		targetId: z.uuid(),
	}),
	z.object({
		targetType: z.literal(ShortUrlTargetType.URL),
		targetUrl: safeRedirectUrl,
	}),
])

export type ShortUrlCreateInput = z.infer<typeof shortUrlCreateSchema>
